import { EnvironmentLogs } from "@/components/EnvironmentLogs";
import { useEnvironment } from "@/queries/environment-queries";
import { useMemo, type FC, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { EnvironmentDetailsHeader } from "@/components/environments/EnvironmentDetailsHeader";
import { BackButton } from "@/components/environments/BackButton";
import { environmentLogsUrl } from "@/utils/environment";
import { IconBox } from "@/assets/icons/geist/IconBox";
import { useEnvironmentDetailsOutletContext } from "@/hooks/use-environment-details-outlet-context";
import { Button } from "@/components/flexkit/Button";
import { IconCheck } from "@/assets/icons/geist/IconCheck";
import { IconCopy } from "@/assets/icons/geist/IconCopy";
import { Tooltip } from "@/components/Tooltip";
import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useStreamLogs } from "@/hooks/use-stream-logs";
import { NO_LOG_GROUP } from "@/routes/environments/log-streams/log-groups";
import { formatError } from "@/utils/errors";

export const EnvironmentLogsPage: FC = () => {
    const { environmentId } = useParams();
    // Title is handled by EnvironmentDetailsLayout using dynamic title

    const { data: environment, error } = useEnvironment(environmentId);
    const { showingAgentConversation } = useEnvironmentDetailsOutletContext();
    const { toast } = useToast();

    const logsUrl = useMemo(() => environmentLogsUrl(environment), [environment]);
    const { logGroups } = useStreamLogs({ environment, logsUrl, maxLinesPerGroup: 10000 });

    const [isCopyingAll, setIsCopyingAll] = useState(false);
    const [copiedAll, setCopiedAll] = useState(false);

    const isLegacyRunner =
        logGroups && Object.keys(logGroups).length === 1 && logGroups[NO_LOG_GROUP]?.lines?.length > 0;

    const handleCopyAllLogs = useCallback(async () => {
        if (!logGroups) return;

        setIsCopyingAll(true);
        setCopiedAll(false);

        try {
            const allLogLines: string[] = [];

            if (isLegacyRunner) {
                // For legacy runner, get all lines from NO_LOG_GROUP
                const lines = logGroups[NO_LOG_GROUP]?.lines ?? [];
                for (const line of lines) {
                    if (line.type === "SectionLogLine" || line.type === "LegacyLogLine") {
                        allLogLines.push(line.line);
                    }
                }
            } else {
                // For modern runner, get all lines from all groups
                for (const logGroup of Object.values(logGroups)) {
                    if (logGroup.id !== NO_LOG_GROUP) {
                        for (const line of logGroup.lines) {
                            if (line.type === "SectionLogLine" || line.type === "LegacyLogLine") {
                                allLogLines.push(line.line);
                            }
                        }
                    }
                }
            }

            const textToCopy = allLogLines.join("\n");
            await navigator.clipboard.writeText(textToCopy);
            setCopiedAll(true);
            toast({ title: "All logs copied to clipboard" });
        } catch (err) {
            toast({
                title: "Failed to copy all logs to clipboard",
                description: formatError(err),
            });
        } finally {
            setIsCopyingAll(false);
            setTimeout(() => setCopiedAll(false), 2000);
        }
    }, [logGroups, isLegacyRunner, toast]);

    if (error) {
        return (
            <div className="flex h-full flex-col items-center justify-center px-4 text-center">
                <IconBox state="open" className="mb-10 h-16 w-16 shrink-0 animate-rotateBox text-content-primary" />
                <h1 className="mb-2 text-xl font-semibold text-content-primary">
                    That environment doesn&#x2019;t exist
                </h1>
                <p className="text-lg text-content-secondary">You can always create a new one.</p>
            </div>
        );
    }

    return (
        <div className="flex size-full flex-col">
            {environmentId && (
                <div className="flex flex-col gap-4">
                    {!showingAgentConversation && <EnvironmentDetailsHeader showActionBar={false} />}
                    <div className="flex justify-between pb-4">
                        <BackButton />
                        {logGroups && Object.keys(logGroups).length > 0 && (
                            <Tooltip content="Copy all logs to clipboard" usePortal={true}>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className={cn("border-0 text-content-primary hover:text-content-primary", {
                                        "disabled:text-content-success": copiedAll,
                                    })}
                                    LeadingIcon={copiedAll ? IconCheck : IconCopy}
                                    disabled={isCopyingAll || copiedAll}
                                    onClick={handleCopyAllLogs}
                                    aria-label="Copy all logs to clipboard"
                                    data-tracking-id="copy-all-logs-environment-logs"
                                >
                                    {isCopyingAll ? "Copying..." : copiedAll ? "Copied!" : "Copy all Logs"}
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                </div>
            )}
            <EnvironmentLogs
                environment={environment}
                logsUrl={logsUrl}
                // We're checking for timestamps here as a way to filter out log lines that supervisor is not
                // intentionally emitting. This allows us to clean up the log output even for legacy runners
                filterLogLinesWithoutTimestamps={true}
            />
        </div>
    );
};
